// Flow login (CEO redesign 2026-10-12 — Flow is now its own system): a black
// brand panel on the left ("flow by other", "Good work. In flow."), the
// sign-in form on warm off-white on the right. Stacks on a phone (panel hidden,
// the lockup on top of the form).
//
// Per Step 3.5: bad password → 401, locked → 429 with retry_at, password
// reset required → 403. We surface each cleanly to the user.
//
// Two-step sign-in (CEO 2026-10-21): when the password is right but a code
// (or first-time setup) is owed, the right-hand panel swaps the form for
// <TwoStepSignIn>. The invite page hands a setup challenge here through
// location.state.twoStep.
import { useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { type TwoStepChallenge, useAuth } from "@/auth/AuthProvider";
import { TwoStepSignIn } from "@/auth/TwoStepSignIn";
import { ApiError, api } from "@/lib/api";
import { FlowLockup } from "@/components/FlowLockup";

export function LoginPage() {
  const { login, adoptSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [challenge, setChallenge] = useState<TwoStepChallenge | null>(
    () => (location.state as { twoStep?: TwoStepChallenge } | null)?.twoStep ?? null,
  );
  const goOn = () => {
    const from = (location.state as { from?: string } | null)?.from ?? "/dashboard";
    navigate(from, { replace: true });
  };
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetSent, setResetSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const outcome = await login(email.trim(), password);
      if (outcome.kind === "two_step") {
        setPassword("");
        setChallenge(outcome.challenge);
        return;
      }
      goOn();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setError("Email or password didn't match. Try again.");
        } else if (err.status === 429) {
          const retryAt = (err.body as { retry_at?: string } | null)?.retry_at;
          setError(
            retryAt
              ? `Too many attempts. Try again at ${new Date(retryAt).toLocaleTimeString()}.`
              : "Too many failed attempts. Please wait 30 minutes and try again.",
          );
        } else if (err.status === 403) {
          setError("Your password needs to be reset. Use 'Forgot password?' below.");
        } else if (err.status === 503 && (err.body as { error?: string } | null)?.error === "mfa_misconfigured") {
          // Two-step is on but broken (key / tables) — sign-in is refused on purpose.
          setError("Two-step sign-in is misconfigured — contact admin.");
        } else if (err.status === 400) {
          setError("Email and password are required.");
        } else {
          setError("Something went wrong. Please try again in a moment.");
        }
      } else {
        setError("Network error. Check your connection and try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = async () => {
    if (!email.trim()) {
      setError("Enter your email above first, then click 'Forgot password?'");
      return;
    }
    setError(null);
    try {
      await api("/auth/password-reset", {
        method: "POST",
        body: JSON.stringify({ email: email.trim() }),
        skipAuth: true,
      });
      setResetSent(true);
    } catch {
      // Per spec, /auth/password-reset always returns 202 even on unknown
      // emails — surface the generic confirmation regardless.
      setResetSent(true);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
      {/* Left half — the Flow brand panel */}
      <div className="relative hidden flex-col justify-between bg-[#0a0a0a] p-10 text-white md:flex lg:p-12">
        <FlowLockup width={112} className="text-white" />
        <div className="px-0 lg:px-10">
          <h1 className="font-display text-[clamp(56px,6vw,92px)] font-bold leading-[0.98] tracking-[-0.05em] text-white">
            Good work.
            <br />
            In flow.
          </h1>
          <p className="mt-8 max-w-md text-[clamp(18px,1.6vw,24px)] leading-[1.35] text-white/65">
            People, projects, and progress.
            <br />
            All moving together.
          </p>
          <p className="mt-12 flex items-center gap-4 text-sm text-white/55">
            <span>Sales</span>
            <span aria-hidden="true">/</span>
            <span>Production</span>
            <span aria-hidden="true">/</span>
            <span>Finance</span>
          </p>
        </div>
        <div className="text-sm text-white/55">© {new Date().getFullYear()} other</div>
      </div>

      {/* Right half — the form */}
      <div className="relative flex items-center justify-center bg-[#f5f4ef] px-6 py-16">
        <div className="w-full max-w-[454px]">
          <div className="mb-10 md:hidden">
            <FlowLockup width={96} className="text-black" />
          </div>
          {challenge ? (
            <TwoStepSignIn
              key={challenge.token}
              challenge={challenge}
              onSignedIn={(token, u) => {
                adoptSession(token, u);
                goOn();
              }}
              onStartOver={() => {
                setChallenge(null);
                setError(null);
                navigate("/login", { replace: true, state: null });
              }}
            />
          ) : (
          <>
          <h2 className="font-display text-[clamp(30px,2.6vw,38px)] font-bold tracking-[-0.03em] text-black">
            Welcome to Flow.
          </h2>
          <p className="mt-2 text-[17px] text-neutral-500">Sign in to your other workspace.</p>

          <form onSubmit={handleSubmit} className="mt-10 space-y-6">
            <div>
              <label htmlFor="email" className="mb-2 block text-[16px] font-semibold text-black">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@withother.com"
                className="h-[52px] w-full rounded-[4px] border border-neutral-300 bg-white px-4 text-[16px] text-black placeholder:text-neutral-400 focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
              />
            </div>
            <div>
              <label htmlFor="password" className="mb-2 block text-[16px] font-semibold text-black">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="h-[52px] w-full rounded-[4px] border border-neutral-300 bg-white pl-4 pr-12 text-[16px] text-black placeholder:text-neutral-400 focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-neutral-600 hover:text-black"
                >
                  <EyeIcon off={showPassword} />
                </button>
              </div>
            </div>

            {error && (
              <div role="alert" className="rounded-[4px] border border-rust-300 bg-rust-50 px-3 py-2 text-sm text-rust-600">
                {error}
              </div>
            )}
            {resetSent && !error && (
              <div role="status" className="rounded-[4px] border border-neutral-300 bg-white px-3 py-2 text-sm text-black">
                If that email is in our system, we sent a reset link. Check your inbox.
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="flex h-[54px] w-full items-center justify-center gap-3 rounded-[3px] bg-black text-[17px] font-semibold text-white transition-colors hover:bg-neutral-800 disabled:cursor-progress disabled:opacity-70"
            >
              {submitting ? "Signing in…" : (<>Sign in <span aria-hidden="true">→</span></>)}
            </button>

            <button
              type="button"
              onClick={handleReset}
              className="text-[15px] text-neutral-700 underline-offset-4 hover:text-black hover:underline"
            >
              Forgot password?
            </button>
          </form>
          </>
          )}
        </div>
        <p className="absolute bottom-10 right-10 hidden text-sm text-neutral-500 md:block lg:bottom-12 lg:right-12">
          The workspace behind other.
        </p>
      </div>
    </div>
  );
}

function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
      {off && <path d="M4 4l16 16" />}
    </svg>
  );
}
